import * as stylex from '@stylexjs/stylex'
import { useState } from 'react'
import { Modal, type ModalProps } from '../Modal/Modal'
import { Paper, type PaperProps } from '../Paper/Paper'
import { breakpointValues } from '../tokens/breakpoints.stylex'
import { useXStyles, type XStyles } from '../useXStyles/useXStyles'

const _baseStyles = stylex.create({
  root: {
    borderRadius: '8px',
    outline: '0',
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center',
    height: '100%',
  },
  paper: {
    margin: '0 16px',
    display: 'flex',
    flexDirection: 'column',
  },
  paperFullWidth: {
    width: '100%',
  },
})

export interface DialogProps extends Omit<ModalProps, 'xStyles'> {
  /**
   * 自訂內部子元素樣式覆寫
   */
  xStyles?: Partial<XStyles<keyof typeof _baseStyles>>
  /**
   * 是否撐滿最大寬度
   * @default false
   */
  fullWidth?: boolean
  /**
   * 對話框最大寬度，可使用斷點名稱或數值
   * @default 'mobile'
   */
  maxWidth?: keyof typeof breakpointValues | number
  /**
   * 對話框最大高度
   * @default 'calc(100% - 64px)'
   */
  maxHeight?: PaperProps['maxHeight']
  /**
   * 對話框最小高度
   */
  minHeight?: PaperProps['minHeight']
  /**
   * 對話框高度
   */
  height?: PaperProps['height']
  /**
   * 對話框 Paper 的 data-testid，供測試定位。
   * @default 'dialog-paper'
   */
  'data-testid'?: string
}

/**
 * Dialog 對話框元件，用於在頁面上方顯示重要互動內容。
 * 基於 Modal 與 Paper 組合而成，支援自訂寬高與全寬模式。
 */
export const Dialog = (props: DialogProps) => {
  const {
    xStyles,
    children,
    maxWidth: maxWidthProp = 'mobile',
    maxHeight = 'calc(100% - 64px)',
    minHeight,
    height,
    fullWidth,
    open,
    'data-testid': dataTestId = 'dialog-paper',
    ...rest
  } = props
  const baseStyles = useXStyles(_baseStyles, xStyles)
  const maxWidth = typeof maxWidthProp === 'string' ? breakpointValues[maxWidthProp] ?? maxWidthProp : maxWidthProp
  const [closeAreaElement, setCloseAreaElement] = useState<HTMLElement | null>(null)

  return (
    <Modal open={open} {...rest} closeAreaElement={closeAreaElement}>
      <div ref={setCloseAreaElement} {...stylex.props(baseStyles.root)}>
        <Paper
          data-testid={dataTestId}
          elevation={8}
          rootStyle={[
            baseStyles.paper,
            fullWidth && baseStyles.paperFullWidth,
          ]}
          maxWidth={maxWidth}
          maxHeight={maxHeight}
          minHeight={minHeight}
          height={height}
          borderRadius={8}
        >
          {children}
        </Paper>
      </div>
    </Modal>
  )
}
